import React from 'react';
import { motion } from 'framer-motion';
import { FiBookOpen, FiClock, FiTrendingUp, FiFileText, FiChevronRight } from 'react-icons/fi';
import { getCurrentWeekNumber } from '../../utils/datetime/datetime-comprehensive';
import InstructorDashboard from './InstructorDashboard';
import AdminDashboard from './AdminDashboard';
import ActiveAttendanceSessions from './ActiveAttendanceSessions';
import ManagedCoursesWidget from './ManagedCoursesWidget';
import styles from './Dashboard.module.css';
import { useDashboard } from './useDashboard';
import DashboardSummaryCards from './dashboard/DashboardSummaryCards';

// Helper function to get stat icon class
const getStatIconClass = (color) => {
  switch (color) {
    case 'primary': return styles.statIcon + ' ' + styles.primary;
    case 'success': return styles.statIcon + ' ' + styles.success;
    case 'warning': return styles.statIcon + ' ' + styles.warning;
    default: return styles.statIcon + ' ' + styles.primary;
  }
};

const formatDashboardDate = () =>
  new Date().toLocaleDateString(undefined, {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
  });

const parseDateOnly = (dateString) => {
  if (!dateString) return null;

  const datePart = String(dateString).split('T')[0];
  const [year, month, day] = datePart.split('-');
  if (!year || !month || !day) return null;

  const date = new Date(parseInt(year, 10), parseInt(month, 10) - 1, parseInt(day, 10));
  return Number.isNaN(date.getTime()) ? null : date;
};

const getCourseEndDate = (item) => {
  return parseDateOnly(
    item.course_end_date ||
    item.courseEndDate ||
    item.end_date ||
    item.endDate
  );
};

const isOngoingCourseItem = (item) => {
  const isActive = item.course_is_active === 1 || item.course_is_active === true;
  if (!isActive) return false;

  const endDate = getCourseEndDate(item);
  if (!endDate) return true;

  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  return endDate >= today;
};

function Dashboard() {
  const {
    allActiveAssignments,
    assignmentSummary,
    enrolledCourses,
    formatDueDate,
    getAssignmentStatus,
    getQuizStatus,
    navigate,
    notesSummary,
    quizSummary,
    quizzes,
    recentActivities,
    recentAssignments,
    recentNotes,
    recentQuizzes,
    setShowPreviousCourses,
    showPreviousCourses,
    stats,
    user,
  } = useDashboard();

  if (user?.role === 'instructor') {
    return <InstructorDashboard />;
  }


  const handleCourseClick = (courseId) => {
    navigate(`/courses/${courseId}`);
  };

  const openAssignmentInline = (assignmentId, weekNumber = null) => {
    if (!assignmentId) {
      return;
    }
    let url = `/assignments?assignmentId=${encodeURIComponent(assignmentId)}`;
    if (weekNumber) {
      url += `&weekNumber=${encodeURIComponent(weekNumber)}`;
    }
    navigate(url);
  };

  const handleAssignmentClick = (assignmentId, weekNumber = null) => {
    openAssignmentInline(assignmentId, weekNumber);
  };

  const handleQuizClick = (assignmentId) => {
    navigate(`/student/assignments/${assignmentId}/warning`);
  };

  const getTotalPendingTasks = () => {
    let pendingCount = 0;

    // Use allActiveAssignments instead of assignments to get ALL active assignments
    // Count pending non-weekly assignments
    const nonWeeklyAssignments = allActiveAssignments.filter(a => !a.allow_weekly_submissions && !a.submitted_at);
    pendingCount += nonWeeklyAssignments.length;

    // Count pending weekly assignments (by week)
    const weeklyAssignments = allActiveAssignments.filter(a => a.allow_weekly_submissions);
    for (const assignment of weeklyAssignments) {
      // Calculate total weeks based on start_date
      let totalWeeks = 0;
      if (assignment.start_date) {
        const breakPeriods = Array.isArray(assignment.break_periods) ? assignment.break_periods : [];
        const currentWeek = getCurrentWeekNumber(assignment.start_date, null, breakPeriods);
        const parsedMaxWeeks = Number(assignment.max_weekly_submissions);
        const maxWeeks = Number.isFinite(parsedMaxWeeks) && parsedMaxWeeks > 0
          ? Math.floor(parsedMaxWeeks)
          : 1;
        totalWeeks = Math.min(currentWeek > 0 ? currentWeek : 0, maxWeeks);
      }

      const submittedWeeks = assignment.submitted_week_numbers || [];
      const weeklyOverridesByWeek = assignment?.weekly_overrides_by_week && typeof assignment.weekly_overrides_by_week === 'object'
        ? assignment.weekly_overrides_by_week
        : {};
      const requiredWeeks = Array.from({ length: totalWeeks }, (_, index) => index + 1)
        .filter(weekNumber => {
          const weekOverride = weeklyOverridesByWeek[weekNumber] ?? weeklyOverridesByWeek[String(weekNumber)];
          return !(weekOverride?.is_extra_credit === true || weekOverride?.is_extra_credit === 1);
        });
      const completedRequiredWeeks = submittedWeeks.filter(weekNumber => requiredWeeks.includes(Number(weekNumber))).length;
      const pendingWeeks = requiredWeeks.length - completedRequiredWeeks;
      pendingCount += Math.max(0, pendingWeeks);
    }

    // Count pending quizzes (quizzes that haven't been started or are in progress)
    const pendingQuizzes = quizzes.filter(q => isOngoingCourseItem(q) && (q.status === 'not_started' || q.status === 'in_progress')).length;
    pendingCount += pendingQuizzes;

    return pendingCount;
  };

  const getTotalCompletedTasks = () => {
    let completedCount = 0;

    // Use allActiveAssignments instead of assignments to get ALL active assignments
    // Count completed non-weekly assignments
    const nonWeeklyAssignments = allActiveAssignments.filter(a => !a.allow_weekly_submissions && a.submitted_at);
    completedCount += nonWeeklyAssignments.length;

    // Count completed weekly assignments (by week)
    const weeklyAssignments = allActiveAssignments.filter(a => a.allow_weekly_submissions);
    for (const assignment of weeklyAssignments) {
      const submittedWeeks = assignment.submitted_week_numbers || [];
      const weeklyOverridesByWeek = assignment?.weekly_overrides_by_week && typeof assignment.weekly_overrides_by_week === 'object'
        ? assignment.weekly_overrides_by_week
        : {};
      completedCount += submittedWeeks.filter(weekNumber => {
        const weekOverride = weeklyOverridesByWeek[Number(weekNumber)] ?? weeklyOverridesByWeek[String(weekNumber)];
        return !(weekOverride?.is_extra_credit === true || weekOverride?.is_extra_credit === 1);
      }).length;
    }

    // Count completed quizzes (only quizzes with status 'completed')
    const completedQuizzes = quizzes.filter(q => isOngoingCourseItem(q) && q.status === 'completed').length;
    completedCount += completedQuizzes;

    return completedCount;
  };

  // Render admin dashboard for admin users
  if (user?.role === 'admin') {
    return <AdminDashboard />;
  }

  return (
    <div className={styles.dashboardContainer}>
      <div className={styles.dashboardHeader}>
        <div className={styles.headerEyebrow}>{formatDashboardDate()}</div>
        <h1 className={styles.headerTitle}>
          <span dir="ltr">{user?.first_name}</span>
          {' '}
          {user?.role === 'instructor' && <span dir="rtl">شيخ </span>}
          <span dir="rtl">السلام عليكم</span>
        </h1>
        <p className={styles.headerSubtitle} style={{ marginTop: '1.5rem' }}>Here's what's happening with your courses today.</p>
      </div>

      {/* Active Attendance Sessions - Only show for students */}
      {user?.role === 'student' && <ActiveAttendanceSessions />}

      {/* Courses with granted permissions */}
      <ManagedCoursesWidget />

      {/* My Assignments and My Quizzes Summary Sections - Only show for students */}
      {user?.role === 'student' && (
        <DashboardSummaryCards
          assignmentSummary={assignmentSummary}
          enrolledCourses={enrolledCourses}
          formatDueDate={formatDueDate}
          getAssignmentStatus={getAssignmentStatus}
          getQuizStatus={getQuizStatus}
          handleAssignmentClick={handleAssignmentClick}
          handleCourseClick={handleCourseClick}
          handleQuizClick={handleQuizClick}
          navigate={navigate}
          notesSummary={notesSummary}
          quizSummary={quizSummary}
          recentAssignments={recentAssignments}
          recentNotes={recentNotes}
          recentQuizzes={recentQuizzes}
          setShowPreviousCourses={setShowPreviousCourses}
          showPreviousCourses={showPreviousCourses}
          user={user}
        />
      )}

      {/* Stats Strip - Unified Module - Only show for students */}
      {user?.role === 'student' && (
        <motion.div
          className={`${styles.summaryCard} ${styles.statsStrip}`}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.5 }}
        >
          <div className={styles.statsStripContent}>
            <div className={styles.statsStripItem}>
              <div className={getStatIconClass('primary')}>
                <FiBookOpen size={24} />
              </div>
              <div className={styles.statContent}>
                <div className={styles.statValue}>{Number(stats.totalCourses) || 0}</div>
                <div className={styles.statLabel}>Active Courses</div>
              </div>
            </div>
            <div className={styles.statsDivider}></div>
            <div className={styles.statsStripItem}>
              <div className={getStatIconClass('warning')}>
                <FiClock size={24} />
              </div>
              <div className={styles.statContent}>
                <div className={styles.statValue}>{getTotalPendingTasks()}</div>
                <div className={styles.statLabel}>Pending Tasks</div>
              </div>
            </div>
            <div className={styles.statsDivider}></div>
            <div className={styles.statsStripItem}>
              <div className={getStatIconClass('success')}>
                <FiTrendingUp size={24} />
              </div>
              <div className={styles.statContent}>
                <div className={styles.statValue}>{getTotalCompletedTasks()}</div>
                <div className={styles.statLabel}>Completed Tasks</div>
              </div>
            </div>
          </div>
        </motion.div>
      )}

      {/* Recent Activity - Only show for students */}
      {user?.role === 'student' && (
        <motion.div
          className={`${styles.summaryCard} ${styles.activityCard}`}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.6 }}
        >
          <div className={styles.summaryHeader}>
            <h3 className={styles.summaryTitle}>
              <FiClock size={20} />
              Recent Activity
            </h3>
          </div>
          <div className={styles.summaryContent}>
            <div className={styles.summaryItems}>
              {recentActivities.length > 0 ? (
                recentActivities.map((activity, index) => {
                  // Map activity types to appropriate icons
                  const getActivityIcon = (type) => {
                    switch (type) {
                      case 'assignment':
                        return <FiFileText size={16} />;
                      case 'course':
                        return <FiBookOpen size={16} />;
                      case 'grade':
                        return <FiTrendingUp size={16} />;
                      default:
                        return <FiClock size={16} />;
                    }
                  };
                  
                  // Determine navigation target for activity
                  const handleActivityClick = () => {
                    if (activity.type === 'course' && activity.course_id) {
                      navigate(`/courses/${activity.course_id}`);
                    } else if (activity.type === 'assignment' && activity.assignment_id) {
                      openAssignmentInline(activity.assignment_id);
                    } else if (activity.type === 'grade' && activity.course_id) {
                      navigate(`/courses/${activity.course_id}`);
                    } else {
                      navigate('/dashboard');
                    }
                  };
                  
                  const hasNavigation = (activity.type === 'course' && activity.course_id) ||
                                       (activity.type === 'assignment' && activity.assignment_id) ||
                                       (activity.type === 'grade' && activity.course_id);
                  
                  return (
                    <div 
                      key={activity.id || `activity-${index}`} 
                      className={hasNavigation ? styles.listRow : styles.summaryItem}
                      onClick={hasNavigation ? handleActivityClick : undefined}
                      role={hasNavigation ? "button" : undefined}
                      tabIndex={hasNavigation ? 0 : undefined}
                      aria-label={hasNavigation ? `${activity.title}, ${activity.time}` : undefined}
                      onKeyDown={hasNavigation ? (e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          handleActivityClick();
                        }
                      } : undefined}
                    >
                      <div className={styles.activityIcon}>
                        {getActivityIcon(activity.type)}
                      </div>
                      <div className={styles.rowMain}>
                        <div className={styles.summaryItemTitle}>{activity.title}</div>
                        <div className={styles.rowMeta}>{activity.time}</div>
                      </div>
                      {hasNavigation && (
                        <div className={styles.rowChevron}>
                          <FiChevronRight size={16} />
                        </div>
                      )}
                    </div>
                  );
                })
              ) : (
                <div className={styles.emptySummary}>
                  <FiClock />
                  <p>No recent activity.</p>
                </div>
              )}
            </div>
          </div>
        </motion.div>
      )}

      {/* Back to Top Button removed - using GlobalScrollToTop from App.js */}
    </div>
  );
}

export default Dashboard;
